import { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { opsApi } from '../../api/client';
import type { ProjectFile, ProjectFileCategory } from '../../api/types';
import {
  useDeleteProjectFile,
  useProjectFiles,
  useReplaceProjectFile,
  useUpdateProjectFile,
  useUploadProjectFile,
} from '../../hooks/useOps';
import { formatDate } from '../../lib/format';

const CATEGORIES: { value: ProjectFileCategory; label: string }[] = [
  { value: 'manual', label: 'Manual Book' },
  { value: 'wiring_diagram', label: 'Wiring Diagram' },
  { value: 'plc_program', label: 'PLC Program' },
  { value: 'layout', label: 'Panel Layout' },
  { value: 'other', label: 'Other' },
];
const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));
const MAX_BYTES = 25 * 1024 * 1024;

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The project's Google-Drive-style document library (manuals, wiring diagrams, PLC programs, layouts, …). */
export function FilesTab({ projectId }: { projectId: string }) {
  const { data: files, isLoading } = useProjectFiles(projectId);
  const upload = useUploadProjectFile(projectId);
  const update = useUpdateProjectFile(projectId);
  const replace = useReplaceProjectFile(projectId);
  const remove = useDeleteProjectFile(projectId);

  const [filter, setFilter] = useState<ProjectFileCategory | 'all'>('all');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Upload dialog state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [upCategory, setUpCategory] = useState<ProjectFileCategory>('manual');
  const [upName, setUpName] = useState('');
  const [upDescription, setUpDescription] = useState('');
  const [upVisible, setUpVisible] = useState(false);

  // Row menu + edit/delete dialogs
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuFile, setMenuFile] = useState<ProjectFile | null>(null);
  const [editFile, setEditFile] = useState<ProjectFile | null>(null);
  const [deleteFile, setDeleteFile] = useState<ProjectFile | null>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const [replaceTarget, setReplaceTarget] = useState<ProjectFile | null>(null);

  const visible = useMemo(
    () => (files ?? []).filter((f) => filter === 'all' || f.category === filter),
    [files, filter],
  );

  const pickFile = (file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setErrorMsg(`"${file.name}" is ${formatSize(file.size)} — over the 25 MB limit`);
      return;
    }
    setPendingFile(file);
    setUpName(file.name);
    setUploadOpen(true);
  };

  const doUpload = async () => {
    if (!pendingFile) return;
    try {
      await upload.mutateAsync({
        file: pendingFile,
        category: upCategory,
        name: upName,
        description: upDescription,
        visibleToClient: upVisible,
      });
      setUploadOpen(false);
      setPendingFile(null);
      setUpDescription('');
      setUpVisible(false);
    } catch (e) {
      setErrorMsg((e as Error).message ?? 'Upload failed');
    }
  };

  const doDownload = async (f: ProjectFile) => {
    try {
      const blob = await opsApi.downloadProjectFile(f.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = f.originalName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErrorMsg((e as Error).message ?? 'Download failed');
    }
  };

  const doReplace = async (file: File | undefined) => {
    if (!file || !replaceTarget) return;
    if (file.size > MAX_BYTES) {
      setErrorMsg(`"${file.name}" is ${formatSize(file.size)} — over the 25 MB limit`);
      return;
    }
    try {
      await replace.mutateAsync({ fileId: replaceTarget.id, file });
    } catch (e) {
      setErrorMsg((e as Error).message ?? 'Replace failed');
    } finally {
      setReplaceTarget(null);
    }
  };

  if (isLoading) return <LinearProgress />;

  return (
    <Stack gap={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            Files
          </Typography>
          <Button variant="contained" component="label" startIcon={<UploadFileIcon />} disabled={upload.isPending}>
            Upload file
            <input
              type="file"
              hidden
              onChange={(e) => {
                pickFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </Button>
        </Stack>

        <ToggleButtonGroup
          size="small"
          value={filter}
          exclusive
          onChange={(_e, v) => v && setFilter(v)}
          sx={{ mb: 2, flexWrap: 'wrap' }}
        >
          <ToggleButton value="all">All</ToggleButton>
          {CATEGORIES.map((c) => (
            <ToggleButton key={c.value} value={c.value}>
              {c.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {visible.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            {files?.length ? 'No files in this category.' : 'No files yet. Upload manuals, wiring diagrams, PLC programs, layouts…'}
          </Typography>
        ) : (
          <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
            {visible.map((f) => (
              <Stack key={f.id} direction="row" gap={1.5} alignItems="center" sx={{ py: 1 }}>
                <InsertDriveFileIcon color="action" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap title={f.originalName}>
                    {f.originalName}
                  </Typography>
                  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    <Chip size="small" variant="outlined" label={CATEGORY_LABEL[f.category] ?? f.category} />
                    <Typography variant="caption" color="text.secondary">
                      {formatSize(f.sizeBytes)} · {formatDate(f.uploadedAt)}
                    </Typography>
                    {f.visibleToClient && (
                      <Tooltip title="Shown on the client portal & share link">
                        <Chip
                          size="small"
                          color="info"
                          variant="outlined"
                          icon={<VisibilityIcon />}
                          label="Client"
                        />
                      </Tooltip>
                    )}
                  </Stack>
                  {f.description && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {f.description}
                    </Typography>
                  )}
                </Box>
                <Tooltip title="Download">
                  <IconButton size="small" onClick={() => doDownload(f)}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    setMenuAnchor(e.currentTarget);
                    setMenuFile(f);
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        )}
      </Paper>

      {/* Row action menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            setEditFile(menuFile);
            setMenuAnchor(null);
          }}
        >
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit details</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setReplaceTarget(menuFile);
            setMenuAnchor(null);
            replaceInputRef.current?.click();
          }}
        >
          <ListItemIcon><SwapHorizIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Replace file</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteFile(menuFile);
            setMenuAnchor(null);
          }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Hidden input for the Replace action */}
      <input
        type="file"
        hidden
        ref={replaceInputRef}
        onChange={(e) => {
          doReplace(e.target.files?.[0]);
          e.target.value = '';
        }}
      />

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Upload file</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              {pendingFile?.name} · {pendingFile ? formatSize(pendingFile.size) : ''}
            </Typography>
            <TextField label="Display name" value={upName} onChange={(e) => setUpName(e.target.value)} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={upCategory}
                onChange={(e) => setUpCategory(e.target.value as ProjectFileCategory)}
              >
                {CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Description (optional)"
              value={upDescription}
              onChange={(e) => setUpDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <FormControlLabel
              control={<Checkbox checked={upVisible} onChange={(e) => setUpVisible(e.target.checked)} />}
              label="Visible to client (portal & share link)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={doUpload} disabled={upload.isPending || !pendingFile}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <EditFileDialog
        file={editFile}
        onClose={() => setEditFile(null)}
        onSave={async (body) => {
          if (!editFile) return;
          try {
            await update.mutateAsync({ fileId: editFile.id, body });
            setEditFile(null);
          } catch (e) {
            setErrorMsg((e as Error).message ?? 'Update failed');
          }
        }}
        saving={update.isPending}
      />

      {/* Delete confirm */}
      <Dialog open={Boolean(deleteFile)} onClose={() => setDeleteFile(null)}>
        <DialogTitle>Delete file?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{deleteFile?.originalName}</strong> will be permanently removed. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFile(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={remove.isPending}
            onClick={async () => {
              if (!deleteFile) return;
              try {
                await remove.mutateAsync(deleteFile.id);
                setDeleteFile(null);
              } catch (e) {
                setErrorMsg((e as Error).message ?? 'Delete failed');
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(errorMsg)}
        autoHideDuration={6000}
        onClose={() => setErrorMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMsg(null)} sx={{ width: '100%' }}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </Stack>
  );
}

function EditFileDialog({
  file,
  onClose,
  onSave,
  saving,
}: {
  file: ProjectFile | null;
  onClose: () => void;
  onSave: (body: { category: ProjectFileCategory; name: string; description: string | null; visibleToClient: boolean }) => void;
  saving: boolean;
}) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState<ProjectFileCategory>('other');
  const [description, setDescription] = useState('');
  const [visible, setVisible] = useState(false);

  // Re-seed local state each time a different file opens the dialog.
  const [seededId, setSeededId] = useState<string | null>(null);
  if (file && file.id !== seededId) {
    setSeededId(file.id);
    setName(file.originalName);
    setCategory(file.category);
    setDescription(file.description ?? '');
    setVisible(file.visibleToClient);
  }

  return (
    <Dialog open={Boolean(file)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit file</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <TextField label="Display name" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select label="Category" value={category} onChange={(e) => setCategory(e.target.value as ProjectFileCategory)}>
              {CATEGORIES.map((c) => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <FormControlLabel
            control={<Checkbox checked={visible} onChange={(e) => setVisible(e.target.checked)} />}
            label="Visible to client (portal & share link)"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={saving}
          onClick={() => onSave({ category, name, description: description.trim() || null, visibleToClient: visible })}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
